import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useSesion } from '@/api/sesion';
import { cn } from '@/lib/utils';
import { tieneAlguno, type SeccionModulo } from './navegacion';

interface Props {
  titulo: string;
  secciones?: SeccionModulo[];
  children: ReactNode;
}

/** Contenedor de un módulo: submenú lateral propio (solo dentro del módulo) + área de contenido. */
export function ModuleLayout({ titulo, secciones, children }: Props) {
  const { usuario } = useSesion();
  const visibles = (secciones ?? []).filter((s) => tieneAlguno(usuario?.permisos ?? [], s.permisos));

  if (visibles.length === 0) return <div className="mx-auto w-full max-w-[1500px] px-4 py-6 sm:px-6">{children}</div>;

  return (
    <div className="mx-auto flex w-full max-w-[1500px] gap-6 px-4 py-6 sm:px-6">
      <aside className="hidden w-52 shrink-0 lg:block" aria-label={`Secciones de ${titulo}`}>
        <p className="mb-2 px-3 font-display text-[13px] italic text-foreground-muted">{titulo}</p>
        <nav className="flex flex-col gap-0.5">
          {visibles.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              activeOptions={{ exact: true }}
              className="group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] text-foreground-muted transition-colors hover:bg-muted hover:text-foreground"
              activeProps={{ className: 'bg-surface text-foreground font-medium shadow-hoja' }}
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      'absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-accent transition-opacity',
                      isActive ? 'opacity-100' : 'opacity-0',
                    )}
                    aria-hidden
                  />
                  <s.icono className="size-4" aria-hidden />
                  {s.etiqueta}
                </>
              )}
            </Link>
          ))}
        </nav>
      </aside>

      <div className="min-w-0 flex-1">
        {/* En pantallas chicas el submenú se vuelve una fila de pestañas desplazable. */}
        <nav className="-mx-1 mb-4 flex gap-1 overflow-x-auto pb-1 lg:hidden" aria-label={`Secciones de ${titulo}`}>
          {visibles.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              activeOptions={{ exact: true }}
              className="shrink-0 rounded-full border border-border px-3 py-1.5 text-[13px] text-foreground-muted"
              activeProps={{ className: 'bg-primary text-on-primary border-primary' }}
            >
              {s.etiqueta}
            </Link>
          ))}
        </nav>
        {children}
      </div>
    </div>
  );
}

/** Encabezado de página: título serif con filete latón y acciones a la derecha. */
export function EncabezadoPagina({ titulo, descripcion, acciones }: { titulo: string; descripcion?: ReactNode; acciones?: ReactNode }) {
  return (
    <header className="mb-5 animate-entrar">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[28px] leading-tight">{titulo}</h1>
          {descripcion && <p className="mt-1 text-[13.5px] text-foreground-muted">{descripcion}</p>}
        </div>
        {acciones && <div className="flex flex-wrap items-center gap-2">{acciones}</div>}
      </div>
      <div className="filete mt-3 w-40" aria-hidden />
    </header>
  );
}
